import { Separation } from 'types';

// Neutral grey used when PDFTron has not (yet) reported an rgb for a preprocessor separation.
export const DEFAULT_SEPARATION_RGB: number[] = [128, 128, 128];

// PDFTron exposes a synthetic "All" plate alongside the real inks; it must never appear in the
// panel or be toggled, and it must be ignored when deciding whether plates have finished loading.
export const SYNTHETIC_ALL_SEPARATION = 'All';

export interface DiscoveredSeparation {
  name: string;
  rgb: number[];
}

/**
 * Builds the separation list shown in the panel. The authoritative ordered name list comes
 * from the CTF preprocessor (`preprocessorNames`) when available, otherwise the names PDFTron
 * discovered in the document are used. `rgb` is always sourced from PDFTron's discovered
 * separations (matched by name) since the preprocessor only provides names.
 *
 * Visibility (`enabled`) precedence: saved session state (`sessionSeparations`, e.g. a reopened
 * inspection) wins, then the auto-select `separationsToHide` recommendation, then visible by
 * default. The synthetic PDFTron "All" plate is always excluded.
 */
export function buildSeparations(
  discovered: DiscoveredSeparation[],
  separationsToHide?: string[],
  preprocessorNames?: string[],
  sessionSeparations?: Separation[],
): Separation[] {
  const cleanDiscovered = discovered.filter((separation) => separation.name !== SYNTHETIC_ALL_SEPARATION);
  const rgbByName = new Map<string, number[]>(cleanDiscovered.map((separation) => [separation.name, separation.rgb]));
  const sessionByName = new Map<string, Separation>(
    (sessionSeparations ?? []).map((separation) => [separation.name, separation]),
  );
  const hidden = new Set(separationsToHide ?? []);

  const names = (
    preprocessorNames && preprocessorNames.length > 0 ? preprocessorNames : cleanDiscovered.map((s) => s.name)
  ).filter((name) => name !== SYNTHETIC_ALL_SEPARATION);

  return names.map((name) => {
    const session = sessionByName.get(name);
    return {
      name,
      enabled: session ? session.enabled : !hidden.has(name),
      rgb: rgbByName.get(name) ?? session?.rgb ?? DEFAULT_SEPARATION_RGB,
    };
  });
}
